import {
  CalendarDaysIcon,
  ChartPieIcon,
  Cog6ToothIcon,
  HeroIconProps,
  SquaresPlusIcon,
  TvIcon,
} from 'components'
import { PersonRunningIcon } from 'components/icons'
import { ReactNode, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { useLimitedResourceCounts, usePermission } from 'utils'
import { isContentPage, isSettingsPage, useCurrentPage } from '../utils/pageUtils'
import { getEventsByStatus, useEvents } from './eventUtils'
import { useFeature } from './featureFlags'
import { RouteName, RoutePath } from './routing'

export interface SideMenuItem {
  comingSoon?: string
  disabled?: boolean
  isCurrent?: boolean
  LeftIcon?: (props: HeroIconProps) => ReactNode
  page: RoutePath
  shortcutKey?: string
  statusDot?: 'warning' | 'danger'
  subItems?: SideMenuItem[]
  text: string
}

export const useSideMenuItems = (): SideMenuItem[] => {
  const currentPage = useCurrentPage()
  const location = useLocation()
  const navigate = useNavigate()
  const permission = usePermission()
  const { events = [] } = useEvents()
  const { limitCounts, warningCounts } = useLimitedResourceCounts()
  const enableContent = useFeature('content')
  const enableOnTheGo = useFeature('on-the-go')

  useEffect(() => {
    if (isContentPage(currentPage?.path) && !enableContent) navigate(RoutePath.Events)
  }, [enableContent, currentPage, navigate])

  const eventsByStatus = getEventsByStatus(events)
  const currentSection = currentPage?.parentPath || currentPage?.path

  return [
    {
      text: 'Events',
      page: RoutePath.Events,
      shortcutKey: 'e',
      LeftIcon: CalendarDaysIcon,
      isCurrent: currentSection === RouteName.Events,
      subItems: [
        {
          text: 'Live',
          page: (RoutePath.Events + '#live') as RoutePath,
          disabled: !eventsByStatus.live.length,
          isCurrent: location.hash === '#live',
        },
        {
          text: 'Upcoming',
          page: (RoutePath.Events + '#upcoming') as RoutePath,
          disabled: !eventsByStatus.upcoming.length,
          isCurrent: location.hash === '#upcoming',
        },
        {
          text: 'Past',
          page: (RoutePath.Events + '#past') as RoutePath,
          disabled: !eventsByStatus.past.length,
          isCurrent: location.hash === '#past',
        },
      ],
    },
    {
      text: 'Facilities',
      page: RoutePath.Facilities,
      shortcutKey: 'f',
      LeftIcon: TvIcon,
      isCurrent: currentSection === RouteName.Facilities,
    },
    {
      text: 'On-the-Go',
      page: RoutePath.OnTheGo,
      shortcutKey: 'o',
      LeftIcon: PersonRunningIcon,
      disabled: !enableOnTheGo,
      isCurrent: currentSection === RouteName.OnTheGo,
      comingSoon: !enableOnTheGo ? 'Capture leads anywhere, anytime. Coming soon.' : undefined,
    },
    {
      text: 'Content',
      page: RoutePath.Templates,
      LeftIcon: SquaresPlusIcon,
      isCurrent: isContentPage(currentSection),
      disabled: !enableContent,
      subItems: [
        {
          text: 'Templates',
          page: RoutePath.Templates,
          isCurrent: currentSection === RouteName.Templates,
        },
        {
          text: 'Traits',
          page: RoutePath.Trait,
          isCurrent: currentSection === RouteName.Trait,
        },
        {
          text: 'Text',
          page: RoutePath.Text,
          isCurrent: currentSection === RouteName.Text,
        },
        {
          text: 'Tools',
          page: RoutePath.Tool,
          isCurrent: currentSection === RouteName.Tool,
        },
        {
          text: 'Media',
          page: RoutePath.ManageMedia,
          isCurrent: currentSection === RouteName.Media,
        },
        {
          text: 'Consents',
          page: RoutePath.RegistrationConsents,
          isCurrent: currentSection === RouteName.RegistrationConsents,
        },
      ],
    },
    {
      text: 'Analytics',
      page: RoutePath.WorkspaceAnalytics,
      shortcutKey: 'a',
      LeftIcon: ChartPieIcon,
      isCurrent: currentSection === RouteName.Analytics,
    },
    ...(permission.workspace === 'admin'
      ? [
          {
            text: 'Settings',
            page: RoutePath.ManageWorkspace,
            shortcutKey: 's',
            LeftIcon: Cog6ToothIcon,
            isCurrent: isSettingsPage(currentSection),
            subItems: [
              {
                text: 'Workspace',
                page: RoutePath.ManageWorkspace,
                isCurrent: currentSection === RouteName.ManageWorkspace,
              },
              {
                text: 'Branding',
                page: RoutePath.ManageWorkspaceBranding,
                isCurrent: currentSection === RouteName.ManageWorkspaceBranding,
              },
              {
                text: 'Members',
                page: RoutePath.ManageWorkspaceUsers,
                isCurrent: currentSection === RouteName.ManageWorkspaceUsers,
              },
              {
                text: 'Integrations',
                page: RoutePath.Integrations,
                isCurrent: currentSection === RouteName.Integrations,
              },
              {
                text: 'Properties',
                page: RoutePath.Properties,
                isCurrent: currentSection === RouteName.Properties,
              },
              {
                text: 'Intelligence',
                page: RoutePath.Intelligence,
                isCurrent: currentSection === RouteName.Intelligence,
              },
              {
                text: 'Subscription',
                page: RoutePath.Subscription,
                isCurrent: currentSection === RouteName.Subscription,
                statusDot: limitCounts
                  ? ('danger' as const)
                  : warningCounts
                    ? ('warning' as const)
                    : undefined,
              },
              {
                text: 'Developer',
                page: RoutePath.Developer,
                isCurrent: currentSection === RouteName.Developer,
              },
            ],
          },
        ]
      : []),
  ]
}
